import { Injectable } from '@angular/core';
import {
  AnalyticsAmountBreakdown,
  AnalyticsBilling,
  AnalyticsFacts,
  AnalyticsKpiSet,
  AnalyticsNewVsReturning,
  AnalyticsOverview,
  AnalyticsSeriesPoint,
  AnalyticsSpecies,
  AnalyticsSummary,
} from '../../domain/models/analytics-summary.model';
import {
  AnalyticsAmountBreakdownDto,
  AnalyticsBillingDto,
  AnalyticsFactsDto,
  AnalyticsKpiSetDto,
  AnalyticsNewVsReturningDto,
  AnalyticsOverviewDto,
  AnalyticsSeriesPointDto,
  AnalyticsSpeciesDto,
  AnalyticsSummaryDto,
} from '../dtos/analytics-summary.dto';

@Injectable()
export class AnalyticsMapper {
  public toAnalyticsSummary(dto: AnalyticsSummaryDto): AnalyticsSummary {
    return {
      hasData: dto.HasData,
      currency: dto.Currency,
      generatedAtUtc: dto.GeneratedAtUtc,
      facts: this.toFacts(dto.Facts),
      overview: this.toOverview(dto.Overview),
      billing: this.toBilling(dto.Billing),
    };
  }

  private toKpiSet(dto: AnalyticsKpiSetDto): AnalyticsKpiSet {
    return { averageTicket: dto.AverageTicket };
  }

  private toSeriesPoint(dto: AnalyticsSeriesPointDto): AnalyticsSeriesPoint {
    return { label: dto.Label, value: dto.Value };
  }

  private toNewVsReturning(dto: AnalyticsNewVsReturningDto): AnalyticsNewVsReturning {
    return {
      newCount: dto.NewCount,
      returningCount: dto.ReturningCount,
      providerNewCount: dto.ProviderNewCount,
      appAccountsGained: dto.AppAccountsGained,
      newPct: dto.NewPct,
      returningPct: dto.ReturningPct,
      providerNewCountTrendPct: dto.ProviderNewCountTrendPct ?? null,
      newCountTrendPct: dto.NewCountTrendPct ?? null,
    };
  }

  private toSpecies(dto: AnalyticsSpeciesDto): AnalyticsSpecies {
    return { label: dto.Label, count: dto.Count, percentage: dto.Percentage };
  }

  private toFacts(dto: AnalyticsFactsDto): AnalyticsFacts {
    return {
      currency: dto.Currency,
      providerName: dto.ProviderName ?? null,
      providerConnected: dto.ProviderConnected,
      providerLastSyncAtUtc: dto.ProviderLastSyncAtUtc ?? null,
      syncedInvoiceCount: dto.SyncedInvoiceCount,
      departmentCount: dto.DepartmentCount,
      professionalCount: dto.ProfessionalCount,
      organizationCreatedAtUtc: dto.OrganizationCreatedAtUtc ?? null,
      historyMonths: dto.HistoryMonths,
      earliestDataYearMonth: dto.EarliestDataYearMonth ?? null,
    };
  }

  private toOverview(dto: AnalyticsOverviewDto): AnalyticsOverview {
    return {
      kpis: this.toKpiSet(dto.Kpis),
      revenueSeries: dto.RevenueSeries.map((point) => this.toSeriesPoint(point)),
      averageTicketSeries: dto.AverageTicketSeries.map((point) => this.toSeriesPoint(point)),
      newVsReturning: this.toNewVsReturning(dto.NewVsReturning),
      patientsBySpecies: dto.PatientsBySpecies.map((species) => this.toSpecies(species)),
      totalActivePatients: dto.TotalActivePatients,
    };
  }

  private toBilling(dto: AnalyticsBillingDto): AnalyticsBilling {
    return {
      billedWithVat: dto.BilledWithVat,
      taxableBase: dto.TaxableBase,
      vat: dto.Vat,
      collected: dto.Collected,
      outstanding: dto.Outstanding,
      overdue: dto.Overdue,
      invoiceCount: dto.InvoiceCount,
      creditNoteCount: dto.CreditNoteCount ?? 0,
      revenueByDepartment: dto.RevenueByDepartment.map((breakdown) => this.toAmountBreakdown(breakdown)),
    };
  }

  private toAmountBreakdown(dto: AnalyticsAmountBreakdownDto): AnalyticsAmountBreakdown {
    return { label: dto.Label, amount: dto.Amount, count: dto.Count, percentage: dto.Percentage };
  }
}
